/*
  Prescription upgrade — cart rules.
  Spec: prescription-cart-cta-spec.md

  Three responsibilities, all cart-only (the PDP is untouched):

    1. Gate the prescription CTA (rendered by cart-modal-item.liquid) so it only
       appears on desktop, for returning visitors, on line items whose product
       carries the custom.magento_prescription metafield.
    2. When the LensAdvisor flow completes and drops Rx items into the cart,
       decrement the source line item by exactly one unit.
    3. When a customer removes an Rx frame, remove every line item sharing its
       _unique_identifier. Removing a lens or ribbon removes only itself.

  The drawer is re-rendered wholesale by <cart-form> through the Section
  Rendering API on every cart mutation, so nothing here can be a one-shot pass —
  the gate is re-applied from a MutationObserver.
*/
(function () {
  'use strict';

  var CFG = window.wxRxConfig || {};
  var CTA_SELECTOR = CFG.ctaSelector || '.wx-rx-cta';
  var DESKTOP_MIN_WIDTH = CFG.desktopMinWidth || 992;

  // Bumped on every edit — check `wxRx.build` in the console against this
  // value to be certain the browser is not serving a cached copy.
  var BUILD = '2026-10-14-upcart';

  var GROUP_PROP = '_unique_identifier';
  var FRAME_TYPE = 'frame';

  var PENDING_KEY = 'wx_rx_pending_source';
  var HANDLED_KEY = 'wx_rx_handled_groups';
  var PENDING_TTL = 30 * 60 * 1000;

  // Upcart V2 public selectors. Its drawer is plain DOM (no shadow root).
  var UPCART_ITEMS = '.upcart-public-cart-items';
  var UPCART_ITEM = '.upcart-public-cart-item';
  var UPCART_REMOVE = '.upcart-public-component-product-tile__remove-button';

  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  function routes() {
    return window.routes || {};
  }

  /* ------------------------------------------------------------------ *
   * Logging — always on, no switches.
   * ------------------------------------------------------------------ */

  var LOG_PREFIX = '----->>>';
  var LOG_STYLE = 'color:#ff7a00;font-weight:bold;';

  // Leading primitives are folded into one orange run; anything else is passed
  // through untouched so objects stay expandable in the console.
  function emit(method, args) {
    var rest = [].slice.call(args);
    var text = [];

    while (rest.length && (typeof rest[0] === 'string' || typeof rest[0] === 'number' || typeof rest[0] === 'boolean')) {
      text.push(String(rest.shift()));
    }

    console[method].apply(
      console,
      ['%c' + LOG_PREFIX + (text.length ? ' ' + text.join(' ') : ''), LOG_STYLE].concat(rest)
    );
  }

  function log() {
    emit('log', arguments);
  }

  function warn() {
    emit('warn', arguments);
  }

  function logGroup(label) {
    if (typeof console.groupCollapsed === 'function') {
      console.groupCollapsed('%c' + LOG_PREFIX + ' ' + label, LOG_STYLE);
    } else {
      log(label);
    }
  }

  function logGroupEnd() {
    if (typeof console.groupEnd === 'function') console.groupEnd();
  }

  /* ------------------------------------------------------------------ *
   * 1. Visitor detection (spec §3)
   *
   * Only ever flips new -> returning. Never returning -> new inside a
   * session: the browser answer is resolved once and locked into
   * sessionStorage, while login is a live signal that can only add.
   * ------------------------------------------------------------------ */

  function resolveVisitorState() {
    var loggedIn = CFG.loggedIn === true;
    var locked = null;

    try {
      if (loggedIn) {
        // Login is a hard "returning" signal. localStorage covers future
        // sessions; sessionStorage means a logout later this session does not
        // take the status away again.
        localStorage.setItem('has_visited_site', 'true');
        sessionStorage.setItem('session_checked', 'true');
      }

      locked = sessionStorage.getItem('session_checked');

      if (locked === null) {
        locked = localStorage.getItem('has_visited_site') ? 'true' : 'false';
        sessionStorage.setItem('session_checked', locked);
        localStorage.setItem('has_visited_site', 'true');
      }
    } catch (error) {
      // Storage blocked (private mode, cookie settings). Fall back to the
      // login signal alone rather than throwing — a first-time visitor with no
      // storage is treated as new, which is the safe direction.
      warn('storage unavailable, falling back to the login signal only', error);
      locked = 'false';
    }

    var returning = locked === 'true' || loggedIn;

    log('visitor signals — browser lock:', locked, '| logged in:', loggedIn, '=> returning:', returning);

    return returning;
  }

  var isReturningVisitor = resolveVisitorState();
  var desktopQuery = window.matchMedia('(min-width: ' + DESKTOP_MIN_WIDTH + 'px)');

  window.wxRxIsReturningVisitor = isReturningVisitor;

  logGroup('boot — build ' + BUILD);
  log('returning visitor:', isReturningVisitor);
  log('desktop >= ' + DESKTOP_MIN_WIDTH + 'px:', desktopQuery.matches, '| viewport:', (window.innerWidth || '?') + 'px');
  log('logged in:', CFG.loggedIn === true);
  log('CTA selector:', CTA_SELECTOR);
  log('filter the console by "' + LOG_PREFIX + '" | wxRx.state() for a full dump');
  logGroupEnd();

  /* ------------------------------------------------------------------ *
   * 2. CTA gating (spec §4)
   *
   * The theme renders the CTA with the `hidden` attribute already set, so it
   * never flashes for mobile users or first-time visitors. Revealing it is a
   * single attribute removal.
   * ------------------------------------------------------------------ */

  function ctaAllowedGlobally() {
    return desktopQuery.matches && isReturningVisitor;
  }

  var warnedAboutOrphanCta = false;

  // Returns why this CTA may not be shown, or null when it may.
  function suppressionReason(cta) {
    if (!desktopQuery.matches) {
      return 'viewport is ' + window.innerWidth + 'px, below the ' + DESKTOP_MIN_WIDTH + 'px desktop cutoff';
    }

    if (!isReturningVisitor) {
      return 'visitor is new (not seen before this session, and not logged in)';
    }

    var line = cta.closest('[data-rx-eligible]');

    if (!line) {
      // The CTA landed outside a line item we annotated. Degrade to the
      // desktop + returning gate rather than suppressing the feature outright.
      if (!warnedAboutOrphanCta) {
        warnedAboutOrphanCta = true;
        warn(
          'CTA is not inside an annotated cart line — the per-product metafield check cannot run, ' +
            'so only the desktop + returning gate applies. Element:',
          cta
        );
      }
      return null;
    }

    if (line.getAttribute('data-rx-eligible') !== 'true') {
      if (line.getAttribute('data-rx-group')) {
        return 'line is already part of prescription set ' + line.getAttribute('data-rx-group');
      }

      return 'product has no custom.magento_prescription value';
    }

    return null;
  }

  // The observer fires on every drawer re-render, so the report is only
  // printed when the outcome actually changes.
  var lastGateSignature = '';

  function findCtas() {
    return [].slice.call(document.querySelectorAll(CTA_SELECTOR));
  }

  function applyCtaGate() {
    var ctas = findCtas();
    var reasons = [];
    var shown = 0;
    var i;

    for (i = 0; i < ctas.length; i++) {
      var cta = ctas[i];
      var reason = suppressionReason(cta);
      var show = reason === null;

      reasons.push(reason);
      if (show) shown++;

      // Written only when the value actually changes — the observer below sees
      // attribute changes, and a blind write would retrigger it forever.
      if (cta.hasAttribute('hidden') === show) {
        if (show) cta.removeAttribute('hidden');
        else cta.setAttribute('hidden', '');
      }
    }

    var signature = ctas.length + '/' + shown + '/' + reasons.join(';');
    if (signature === lastGateSignature) return;
    lastGateSignature = signature;

    if (!ctas.length) {
      log('CTA gate: no "' + CTA_SELECTOR + '" in the DOM (no cart line carries a prescription metafield)');
      return;
    }

    logGroup('CTA gate: ' + shown + ' of ' + ctas.length + ' shown');
    log('desktop:', desktopQuery.matches, '| returning:', isReturningVisitor, '| logged in:', CFG.loggedIn === true);

    for (i = 0; i < ctas.length; i++) {
      var itemLine = ctas[i].closest('[data-rx-eligible]');
      log(
        'cta #' + (i + 1),
        reasons[i] === null ? 'SHOWN' : 'HIDDEN — ' + reasons[i],
        '| line key:',
        itemLine ? itemLine.getAttribute('data-cart-line-key') : '(no annotated line)',
        '| metafield:',
        itemLine ? itemLine.getAttribute('data-rx-meta') || '(empty)' : 'unknown'
      );
    }

    logGroupEnd();
  }

  var gateScheduled = false;

  function scheduleCtaGate() {
    if (gateScheduled) return;
    gateScheduled = true;
    requestAnimationFrame(function () {
      gateScheduled = false;
      applyCtaGate();
    });
  }

  function watchForCtas() {
    scheduleCtaGate();

    // The drawer replaces its children wholesale on every cart change, so the
    // gate has to re-run after each re-render (spec §4).
    new MutationObserver(scheduleCtaGate).observe(document.body, {
      childList: true,
      subtree: true
    });

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', scheduleCtaGate);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(scheduleCtaGate);
    }
  }

  /* ------------------------------------------------------------------ *
   * Cart helpers
   * ------------------------------------------------------------------ */

  function postOptions(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    };
  }

  function getCart() {
    return nativeFetch(routes().cart_url || '/cart.js', { credentials: 'same-origin' }).then(function (response) {
      return response.json();
    });
  }

  // One retry before giving up: these calls fire straight after the app's own
  // add-to-cart, so a transient cart lock is the likeliest failure.
  function postCart(url, body) {
    function attempt(remaining) {
      return nativeFetch(url, postOptions(body))
        .then(function (response) {
          return response.json().then(function (data) {
            if (response.ok) return data;
            throw new Error(data.description || data.message || 'Cart request failed');
          });
        })
        .catch(function (error) {
          if (remaining > 0) return attempt(remaining - 1);
          throw error;
        });
    }

    return attempt(1);
  }

  function propertyValue(item, name) {
    var properties = item && item.properties;
    if (!properties) return null;

    if (Array.isArray(properties)) {
      for (var i = 0; i < properties.length; i++) {
        if (properties[i] && properties[i][0] === name) return properties[i][1];
      }
      return null;
    }

    return properties[name] || null;
  }

  function isRxItem(item) {
    return Boolean(propertyValue(item, GROUP_PROP) || propertyValue(item, 'Prescription Type'));
  }

  // Only the frame stands in for the product being upgraded. Lenses and add-ons
  // are extras that ride along with it, so an add that carries no frame must
  // leave the cart alone.
  function isFrameItem(item) {
    return String(propertyValue(item, '_productType') || '').toLowerCase() === 'frame';
  }

  function sectionId(element) {
    var section = element && element.closest ? element.closest('.shopify-section') : null;
    return section && section.id ? section.id.replace('shopify-section-', '') : null;
  }

  function drawerSectionId() {
    return sectionId(document.querySelector('cart-modal'));
  }

  function mainCartSectionId() {
    return sectionId(document.querySelector('.cart-items'));
  }

  function sectionsToRequest() {
    var ids = ['cart-bubble'];
    var drawer = drawerSectionId();
    var main = mainCartSectionId();

    if (drawer) ids.push(drawer);
    if (main) ids.push(main);

    return ids;
  }

  // Redrawing the drawer calls content.replaceChildren(), which throws away the
  // <cart-form> element along with anything patched onto it. The prescription
  // app owns bundle quantities by replacing that element's updateQuantity with
  // its own, as an own property, and only re-applies it from inside its own
  // handler — so once the element goes, quantity changes stop routing through
  // the app for the rest of the page's life and the bundle silently stops
  // staying in step. Carry the patch across our redraw.
  function preserveCartFormPatch() {
    var form = document.querySelector('cart-form');
    var patched =
      form && Object.prototype.hasOwnProperty.call(form, 'updateQuantity')
        ? form.updateQuantity
        : null;

    if (!patched) return function () {};

    return function restore() {
      var fresh = document.querySelector('cart-form');
      if (!fresh || fresh === form) return;
      if (Object.prototype.hasOwnProperty.call(fresh, 'updateQuantity')) return;

      fresh.updateQuantity = patched;
      log('restored the cart form quantity patch that the redraw discarded');
    };
  }

  function renderSections(sections) {
    if (!sections) return;

    var modal = document.querySelector('cart-modal');
    var drawerId = drawerSectionId();
    var restore = preserveCartFormPatch();

    if (modal && drawerId && sections[drawerId] && typeof modal.updateFromSectionHtml === 'function') {
      modal.updateFromSectionHtml(sections[drawerId]);
    } else if (modal && typeof modal.reloadContent === 'function') {
      // reloadContent fetches the section itself, so the new form only exists
      // once it settles.
      var reloaded = modal.reloadContent();
      if (reloaded && typeof reloaded.then === 'function') reloaded.then(restore, restore);
    }

    var mainId = mainCartSectionId();
    if (mainId && sections[mainId]) {
      var wrapper = document.getElementById('shopify-section-' + mainId);
      var incoming = new DOMParser()
        .parseFromString(sections[mainId], 'text/html')
        .getElementById('shopify-section-' + mainId);

      if (wrapper && incoming) wrapper.innerHTML = incoming.innerHTML;
    }

    restore();

    if (sections['cart-bubble']) updateCartBubble(sections['cart-bubble']);

    log('sections re-rendered:', Object.keys(sections).join(', '));

    scheduleCtaGate();
    if (upcartIsOpen()) refreshUpcart();
  }

  function updateCartBubble(html) {
    var bubble = document.getElementById('CartBubble');
    if (!bubble) return;

    var count = String(html).match(/\d+/);
    if (!count) return;

    bubble.classList.toggle('hidden', count[0] === '0');
    bubble.innerHTML = count[0];
  }

  function setBusy(busy) {
    if (upcartIsOpen()) {
      var upcartItems = document.querySelector(UPCART_ITEMS);
      if (upcartItems) upcartItems.classList.toggle('wx-rx-busy', busy);
    }

    var modal = document.querySelector('cart-modal');

    if (modal && typeof modal.showLoadingOverlay === 'function') {
      if (busy) modal.showLoadingOverlay();
      else modal.hideLoadingOverlay();
      return;
    }

    var host = document.getElementById('CartModal-ScrollBody');
    if (!host) return;

    var existing = host.querySelector('.wx-rx-loader-overlay');

    if (!busy) {
      if (existing) existing.remove();
      return;
    }

    if (existing) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

    var overlay = document.createElement('div');
    overlay.className = 'wx-rx-loader-overlay';
    overlay.innerHTML = '<div class="wx-rx-loader-spinner"></div>';
    host.appendChild(overlay);
  }

  // Spec §5 says not to fail silently. Pull the true cart back from the server
  // so the customer is never looking at a stale drawer, and say so in the UI.
  function surfaceCartError(message) {
    warn(message);
    if (upcartIsOpen()) refreshUpcart();

    var modal = document.querySelector('cart-modal');
    if (modal && typeof modal.reloadContent === 'function') {
      var restore = preserveCartFormPatch();
      var reloaded = modal.reloadContent();
      if (reloaded && typeof reloaded.then === 'function') reloaded.then(restore, restore);
      else restore();
    }

    var host = document.getElementById('CartModal-ScrollBody');
    if (!host) return;

    var box = host.querySelector('.wx-rx-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'wx-rx-error';
      box.setAttribute('role', 'alert');
      host.prepend(box);
    }

    box.textContent = (window.cartStrings && window.cartStrings.error) || 'Something went wrong updating your cart.';
  }

  /* ------------------------------------------------------------------ *
   * 3. Remembering which line the customer clicked (spec §5)
   *
   * Matching the finished Rx set back to its source by product id alone is
   * ambiguous — the Rx frame can carry the same variant as the item it came
   * from, and requirement 7 allows several eligible products in one cart.
   * Recording the clicked line up front removes the guesswork.
   * ------------------------------------------------------------------ */

  function rememberSource(line) {
    var pending = {
      key: line.getAttribute('data-cart-line-key'),
      productId: line.getAttribute('data-rx-product-id'),
      variantId: line.getAttribute('data-rx-variant-id'),
      at: Date.now()
    };

    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (error) {
      /* fall through to the id-matching path below */
    }

    log('CTA clicked — source line remembered:', pending.key, '| variant:', pending.variantId, pending);
  }

  function readSource() {
    try {
      var pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      if (!pending || Date.now() - pending.at > PENDING_TTL) return null;
      return pending;
    } catch (error) {
      return null;
    }
  }

  // A single Rx set must only ever cost the source line one unit, however many
  // times the add is observed. The group id is the stable identity across
  // wrappers, retries and page loads within the session.
  function readHandledGroups() {
    try {
      var stored = JSON.parse(sessionStorage.getItem(HANDLED_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (error) {
      return [];
    }
  }

  function isGroupHandled(group) {
    return Boolean(group) && readHandledGroups().indexOf(group) !== -1;
  }

  function markGroupHandled(group) {
    if (!group) return;
    var groups = readHandledGroups();
    if (groups.indexOf(group) !== -1) return;
    groups.push(group);
    try {
      sessionStorage.setItem(HANDLED_KEY, JSON.stringify(groups.slice(-20)));
    } catch (error) {
      /* the in-memory signature check below still covers the common case */
    }
  }

  function releaseGroup(group) {
    if (!group) return;
    try {
      sessionStorage.setItem(
        HANDLED_KEY,
        JSON.stringify(
          readHandledGroups().filter(function (entry) {
            return entry !== group;
          })
        )
      );
    } catch (error) {
      /* nothing to release */
    }
  }

  function clearSource() {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      var cta = event.target.closest && event.target.closest(CTA_SELECTOR);
      if (!cta) return;

      var line = cta.closest('[data-cart-line-key]');
      if (line) rememberSource(line);
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * 4. Swap on flow completion (spec §5)
   *
   * Nothing is touched while the flow is open — if the customer abandons it,
   * the cart is left exactly as it was. The decrement only happens once
   * LensAdvisor has actually put Rx items in the cart.
   * ------------------------------------------------------------------ */

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartAdd(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(url || '');
  }

  // The LensAdvisor markup marks the originating product with a
  // reference-product attribute, sometimes inside a shadow root.
  function deepQueryAll(selector, root) {
    var results = [];

    (function walk(node) {
      if (!node || !node.querySelectorAll) return;

      results.push.apply(results, node.querySelectorAll(selector));

      node.querySelectorAll('*').forEach(function (element) {
        if (element.shadowRoot) walk(element.shadowRoot);
      });
    })(root || document);

    return results;
  }

  function readReferenceIds() {
    return deepQueryAll('[reference-product]')
      .map(function (node) {
        return node.getAttribute('reference-product');
      })
      .filter(Boolean)
      .map(String);
  }

  /* ------------------------------------------------------------------ *
   * cart.referrence_product
   *
   * The metafield lives on the prescription FRAME product and names the
   * product whose cart line must lose a unit. Metafields are only readable in
   * Liquid, and the frame line does not exist in the rendered drawer until the
   * cart re-renders — so the drawer section is fetched fresh and the
   * data-rx-ref-* attributes emitted by cart-modal-item.liquid are read back
   * out of it.
   * ------------------------------------------------------------------ */

  function fetchCartSectionHtml() {
    var id = drawerSectionId() || mainCartSectionId();
    if (!id || !nativeFetch) return Promise.resolve('');

    return nativeFetch(window.location.pathname + '?sections=' + encodeURIComponent(id), {
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        return (data && data[id]) || '';
      })
      .catch(function (error) {
        warn('could not re-render the cart section to read cart.referrence_product', error);
        return '';
      });
  }

  function readReferenceProduct(html, group) {
    if (!html) return null;

    var doc;
    try {
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch (error) {
      return null;
    }

    var nodes = [].slice.call(doc.querySelectorAll('[data-rx-ref-handle], [data-rx-ref-id]')).filter(
      function (node) {
        return node.getAttribute('data-rx-ref-handle') || node.getAttribute('data-rx-ref-id');
      }
    );

    if (!nodes.length) {
      log(
        'no cart.referrence_product on the frame line — falling back to the clicked line, ' +
          'then to product ids'
      );
      return null;
    }

    // Several Rx sets can sit in one cart, so prefer the frame line belonging
    // to the group that was just added.
    var exact = group
      ? nodes.filter(function (node) {
          return node.getAttribute('data-rx-group') === group;
        })[0]
      : null;

    var node = exact || nodes[0];
    var reference = {
      handle: node.getAttribute('data-rx-ref-handle') || '',
      id: node.getAttribute('data-rx-ref-id') || ''
    };

    if (!reference.handle && !reference.id) return null;

    log(
      'reference product from cart.referrence_product:',
      reference.handle || reference.id,
      exact ? '(exact group match)' : '(first frame line found)'
    );

    return reference;
  }

  function findSourceLine(cart, rxItems, reference) {
    // An Rx line can share a variant with the item it replaced, so those are
    // excluded first — otherwise the decrement lands on the set just added.
    var candidates = cart.items.filter(function (item) {
      return !isRxItem(item);
    });

    log('source search:', cart.items.length, 'cart line(s) |', candidates.length, 'non-Rx candidate(s)');

    if (!candidates.length) {
      log('every line in the cart is a prescription line — nothing to decrement');
      return null;
    }

    var pending = readSource();

    // 1. cart.referrence_product names the product to reduce. Preferred: it is
    //    server-side truth and works no matter how the flow was started.
    if (reference) {
      var byMetafield = candidates.filter(function (item) {
        return (
          (reference.handle && String(item.handle) === String(reference.handle)) ||
          (reference.id && String(item.product_id) === String(reference.id))
        );
      });

      if (byMetafield.length) {
        // If the customer clicked the CTA on one specific line of that product,
        // reduce that exact line rather than the first one.
        var clicked =
          pending &&
          byMetafield.filter(function (item) {
            return item.key === pending.key;
          })[0];

        var chosen = clicked || byMetafield[0];
        log(
          'source matched by cart.referrence_product:',
          reference.handle || reference.id,
          '=>',
          chosen.key,
          clicked ? '(the line whose CTA was clicked)' : '(first line of that product)'
        );
        return chosen;
      }

      warn(
        'cart.referrence_product points at',
        reference.handle || reference.id,
        'but no matching product is in the cart — falling back to the clicked line'
      );
    }

    if (!pending) {
      log(
        'no remembered source line — the flow was not started from the cart CTA, ' +
          'so the decrement has to guess from product ids'
      );
    }

    if (pending && pending.key) {
      var byKey = candidates.filter(function (item) {
        return item.key === pending.key;
      })[0];

      if (byKey) {
        log('source matched by remembered line key:', pending.key);
        return byKey;
      }

      log('remembered key', pending.key, 'is no longer in the cart — falling back to id matching');
    }

    var ids = readReferenceIds();
    if (pending && pending.variantId) ids.push(String(pending.variantId));
    if (pending && pending.productId) ids.push(String(pending.productId));

    var byReference = candidates.filter(function (item) {
      return ids.indexOf(String(item.variant_id)) !== -1 || ids.indexOf(String(item.product_id)) !== -1;
    })[0];

    if (byReference) {
      log('source matched by reference-product / pending ids:', ids.join(', '), '=>', byReference.key);
      return byReference;
    }

    // Last resort: the Rx frame normally mirrors the source frame's variant.
    var frameIds = [];
    rxItems.forEach(function (item) {
      if (item.variant_id) frameIds.push(String(item.variant_id));
      if (item.product_id) frameIds.push(String(item.product_id));
    });

    var byFrameVariant =
      candidates.filter(function (item) {
        return frameIds.indexOf(String(item.variant_id)) !== -1 || frameIds.indexOf(String(item.product_id)) !== -1;
      })[0] || null;

    if (byFrameVariant) log('source matched by Rx frame variant fallback:', byFrameVariant.key);
    else warn('no source line could be matched from', ids.length, 'reference id(s) or the Rx frame variant');

    return byFrameVariant;
  }

  function decrementSource(frames) {
    var group = propertyValue(frames[0], GROUP_PROP);

    return Promise.all([getCart(), fetchCartSectionHtml()])
      .then(function (results) {
        var cart = results[0];
        var source = findSourceLine(cart, frames, readReferenceProduct(results[1], group));

        if (!source) {
          warn('no source line matched — cart left unchanged, nothing was decremented');
          releaseGroup(group);
          return null;
        }

        setBusy(true);
        log(
          'decrementing source:',
          source.product_title || source.title || source.key,
          '| qty',
          source.quantity,
          '->',
          Math.max(0, source.quantity - 1),
          '| key:',
          source.key
        );

        // A decrement, not a removal (spec §5): qty 3 becomes qty 2 and the
        // remaining units stay in the cart as regular products.
        return postCart(routes().cart_change_url || '/cart/change.js', {
          id: source.key,
          quantity: Math.max(0, source.quantity - 1),
          sections: sectionsToRequest(),
          sections_url: window.location.pathname
        });
      })
      .then(function (data) {
        if (!data) return;
        log('decrement applied — cart now holds', data.item_count, 'item(s)');
        renderSections(data.sections);
      })
      .catch(function (error) {
        surfaceCartError('Could not decrement the source line item: ' + error.message);
      })
      .then(function () {
        clearSource();
        setBusy(false);
      });
  }

  var lastHandledSignature = null;
  var lastHandledAt = 0;

  function handleCartAddResponse(data) {
    var items = Array.isArray(data && data.items) ? data.items : data ? [data] : [];
    var rxItems = items.filter(isRxItem);

    if (!rxItems.length) {
      log('cart add observed —', items.length, 'item(s), none prescription. Cart left alone.');
      return;
    }

    // The page stacks several fetch wrappers and an XHR hook, so the same add
    // can be observed more than once. Act on it exactly once.
    var signature = rxItems
      .map(function (item) {
        return item.key || item.variant_id;
      })
      .join('|');

    if (signature === lastHandledSignature && Date.now() - lastHandledAt < 5000) {
      log('duplicate cart add observation ignored');
      return;
    }

    lastHandledSignature = signature;
    lastHandledAt = Date.now();

    // Lenses, coatings and other add-ons arrive as their own lines and can be
    // added on their own. Only a frame means a product was actually upgraded,
    // so only a frame costs the source line a unit.
    var frames = rxItems.filter(isFrameItem);

    if (!frames.length) {
      log(
        'prescription items added (' + rxItems.length + ') but no frame in this request — cart left alone'
      );
      return;
    }

    var addedGroup = propertyValue(frames[0], GROUP_PROP);

    if (isGroupHandled(addedGroup)) {
      log('prescription set', addedGroup, 'has already been decremented — ignoring this add');
      return;
    }

    // Claimed before the async work starts, so two adds in flight cannot both
    // decrement. Released again below if no source line is found.
    markGroupHandled(addedGroup);

    logGroup('prescription frame added');
    log('frame(s):', frames.length, '| group:', addedGroup);
    frames.forEach(function (item, index) {
      log('  frame #' + (index + 1), '| variant:', item.variant_id, '| product:', item.product_id);
    });
    logGroupEnd();

    return decrementSource(frames);
  }

  if (nativeFetch) {
    window.fetch = function (input) {
      var promise = nativeFetch.apply(this, arguments);
      var url = requestUrl(input);

      // Only observe cart adds. Attaching handlers to every fetch on the page
      // means unrelated third-party failures — an ad blocker cancelling a
      // tracking request, say — surface here as if they were ours.
      if (url && !isCartAdd(url)) return promise;

      promise
        .then(function (response) {
          if (!response || !response.ok) return;
          if (!isCartAdd(url) && !isCartAdd(response.url)) return;

          return response
            .clone()
            .json()
            .then(handleCartAddResponse);
        })
        .catch(function (error) {
          warn('cart add observation failed', error);
        });

      return promise;
    };
    log('watching fetch for /cart/add.js');
  } else {
    warn('window.fetch unavailable — the prescription swap cannot run');
  }

  // Not every cart add goes through fetch: axios, which several Shopify apps
  // bundle, uses XMLHttpRequest in the browser by default. Without this hook
  // the swap would never fire for those adds.
  (function watchXhrCartAdds() {
    var XHR = window.XMLHttpRequest;
    if (!XHR || !XHR.prototype || !XHR.prototype.open) return;

    var open = XHR.prototype.open;
    var send = XHR.prototype.send;

    XHR.prototype.open = function (method, url) {
      try {
        this.__wxRxCartAdd = isCartAdd(String(url || ''));
      } catch (error) {
        this.__wxRxCartAdd = false;
      }

      return open.apply(this, arguments);
    };

    XHR.prototype.send = function () {
      if (this.__wxRxCartAdd) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;

          try {
            handleCartAddResponse(JSON.parse(this.responseText));
          } catch (error) {
            log('could not read an XHR cart add response', error);
          }
        });
      }

      return send.apply(this, arguments);
    };

    log('watching XMLHttpRequest for /cart/add.js');
  })();

  /* ------------------------------------------------------------------ *
   * 5. Cascade removal (spec §6)
   *
   * Removing the frame takes the whole set with it; removing a lens or
   * ribbon takes only itself, which is what the theme already does, so we
   * only intervene for frames.
   * ------------------------------------------------------------------ */

  function removeRxGroup(group, fallbackHref) {
    setBusy(true);

    getCart()
      .then(function (cart) {
        var updates = {};
        var matched = 0;

        cart.items.forEach(function (item) {
          if (propertyValue(item, GROUP_PROP) === group) {
            updates[item.key] = 0;
            matched++;
          }
        });

        if (!matched) throw new Error('no line items found for group ' + group);

        log('cascade: removing group', group, '—', matched, 'line item(s):', Object.keys(updates).join(', '));

        // One keyed update, not a sequence of line-index changes: indices
        // shift as items disappear, which is what breaks removal when there
        // is more than one Rx set in the cart.
        return postCart(routes().cart_update_url || '/cart/update.js', {
          updates: updates,
          sections: sectionsToRequest(),
          sections_url: window.location.pathname
        });
      })
      .then(function (data) {
        log('cascade removal applied — cart now holds', data.item_count, 'item(s)');
        renderSections(data.sections);
        setBusy(false);
      })
      .catch(function (error) {
        setBusy(false);
        warn('Cascade removal failed, falling back to the plain remove link:', error.message);
        if (fallbackHref) window.location.href = fallbackHref;
        else surfaceCartError('Could not remove the prescription set.');
      });
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('[data-button-remove]');
      if (!button) return;

      var line = button.closest('[data-rx-product-type]');
      if (!line) return;

      var type = (line.getAttribute('data-rx-product-type') || '').toLowerCase();
      var group = line.getAttribute('data-rx-group');

      log('remove clicked — _productType:', type || '(none)', '| group:', group || '(none)');

      if (type !== FRAME_TYPE || !group) {
        log('not a prescription frame — leaving removal to the theme (removes this line only)');
        return;
      }

      // Capture phase: stopPropagation here keeps <cart-form>'s own listener,
      // which is bound directly to this button, from removing the frame on
      // its own before the group update lands.
      event.preventDefault();
      event.stopPropagation();

      removeRxGroup(group, button.getAttribute('href'));
    },
    true
  );

  // Separate from the theme listener above. It only runs while the Upcart
  // drawer is open, and only for Upcart's own remove button.
  document.addEventListener(
    'click',
    function (event) {
      if (!upcartIsOpen()) return;

      var button = event.target.closest && event.target.closest(UPCART_REMOVE);
      if (!button || !button.closest(UPCART_ITEM)) return;

      var line = button.closest(UPCART_ITEM);
      var type = ((line && line.getAttribute('data-rx-product-type')) || '').toLowerCase();
      var group = line && line.getAttribute('data-rx-group');

      if (type !== FRAME_TYPE || !group) return;

      event.preventDefault();
      event.stopPropagation();
      removeRxGroup(group, null);
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * 6. Upcart
   *
   * Upcart renders its own line items and cannot read metafields, but the
   * theme drawer section is still rendered for the same cart, and its lines
   * carry every data-rx-* attribute. Those are copied onto the matching Upcart
   * line and the CTA is injected there, so the gate, the click memory and the
   * frame cascade above apply unchanged.
   * ------------------------------------------------------------------ */

  var RX_ATTRS = [
    'data-cart-line-key',
    'data-rx-product-id',
    'data-rx-variant-id',
    'data-rx-eligible',
    'data-rx-meta',
    'data-rx-ref-handle',
    'data-rx-ref-id',
    'data-rx-product-type',
    'data-rx-group'
  ];

  var upcartLines = [];
  var upcartFetchId = 0;
  var upcartDecorateScheduled = false;

  function upcartIsOpen() {
    var popup = document.getElementById('UpcartPopup');
    return !!(popup && popup.classList.contains('upcartPopupShow'));
  }

  function refreshUpcart() {
    if (typeof window.upcartRefreshCart !== 'function') return;
    try {
      window.upcartRefreshCart();
    } catch (error) {
      warn('upcartRefreshCart failed', error);
    }
  }

  function loadUpcartLines() {
    var fetchId = ++upcartFetchId;

    return fetchCartSectionHtml().then(function (html) {
      // A newer cart event already asked for fresher data.
      if (fetchId !== upcartFetchId) return;

      var doc = html ? new DOMParser().parseFromString(html, 'text/html') : null;

      upcartLines = doc
        ? [].map.call(doc.querySelectorAll('[data-cart-line-key]'), function (node) {
            var data = {};
            RX_ATTRS.forEach(function (name) {
              data[name] = node.getAttribute(name) || '';
            });
            return data;
          })
        : [];

      log('Upcart: read', upcartLines.length, 'line(s) of prescription data from the theme cart section');
      decorateUpcart();
    });
  }

  function tileVariantId(tile) {
    var link = tile.querySelector('a[href*="variant="]');
    var match = link && /[?&]variant=(\d+)/.exec(link.getAttribute('href'));
    return match ? match[1] : '';
  }

  // Upcart lists lines in cart order, so the same index is tried first. The
  // variant check catches a reorder or a list that is mid-update.
  function matchLine(tile, index, used) {
    var variant = tileVariantId(tile);
    var sameIndex = upcartLines[index];

    if (sameIndex && !used[index] && (!variant || sameIndex['data-rx-variant-id'] === variant)) {
      used[index] = true;
      return sameIndex;
    }

    for (var i = 0; i < upcartLines.length; i++) {
      if (!used[i] && upcartLines[i]['data-rx-variant-id'] === variant) {
        used[i] = true;
        return upcartLines[i];
      }
    }

    return null;
  }

  function decorateUpcartTile(tile, line) {
    RX_ATTRS.forEach(function (name) {
      var value = line ? line[name] : null;
      if (value === null) {
        if (tile.hasAttribute(name)) tile.removeAttribute(name);
      } else if (tile.getAttribute(name) !== value) {
        tile.setAttribute(name, value);
      }
    });

    var cta = tile.querySelector(CTA_SELECTOR);
    var url = line && line['data-rx-eligible'] === 'true' ? line['data-rx-meta'] : '';

    if (!url) {
      if (cta) cta.remove();
      return;
    }

    if (!cta) {
      cta = document.createElement('a');
      cta.className = 'wx-rx-cta button magento-rx mt-3';
      cta.textContent = 'Add Prescription';
      // Same contract as the theme markup: inserted hidden, revealed by the gate.
      cta.setAttribute('hidden', '');

      var info = tile.querySelector('.upcart-public-component-product-tile__info') || tile;
      var pricing = info.querySelector('.upcart-public-component-product-tile__product-pricing');

      if (pricing && pricing.parentNode === info) info.insertBefore(cta, pricing);
      else info.appendChild(cta);
    }

    if (cta.getAttribute('href') !== url) cta.setAttribute('href', url);
  }

  function decorateUpcart() {
    var tiles = document.querySelectorAll(UPCART_ITEM);
    if (!tiles.length) return;

    var used = {};
    [].forEach.call(tiles, function (tile, index) {
      decorateUpcartTile(tile, matchLine(tile, index, used));
    });

    scheduleCtaGate();
  }

  function scheduleUpcartDecorate() {
    if (upcartDecorateScheduled) return;
    upcartDecorateScheduled = true;
    requestAnimationFrame(function () {
      upcartDecorateScheduled = false;
      decorateUpcart();
    });
  }

  function bindUpcart() {
    if (typeof window.upcartSubscribeCartUpdated !== 'function') return false;

    window.upcartSubscribeCartUpdated(loadUpcartLines);
    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(loadUpcartLines);
    }

    // Upcart re-renders its line items on its own schedule, which can drop
    // the injected CTA; put it back from the cached data.
    new MutationObserver(function () {
      if (document.querySelector(UPCART_ITEM)) scheduleUpcartDecorate();
    }).observe(document.body, { childList: true, subtree: true });

    log('Upcart detected — prescription CTA bridged into its line items');
    loadUpcartLines();
    return true;
  }

  function watchForUpcart() {
    if (bindUpcart()) return;

    // Upcart loads after this script, and not at all when it is switched off.
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (bindUpcart() || tries > 100) clearInterval(timer);
    }, 100);
  }

  /* ------------------------------------------------------------------ */

  // Console handle for QA: wxRx.state() dumps the current decision inputs,
  // wxRx.on() / wxRx.off() flip logging without a reload.
  // Console handle: wxRx.state() dumps everything the gate decides on.
  window.wxRx = {
    build: BUILD,

    state: function () {
      var lines = document.querySelectorAll('[data-rx-eligible]');
      var report = {
        build: BUILD,
        returningVisitor: isReturningVisitor,
        loggedIn: CFG.loggedIn === true,
        desktop: desktopQuery.matches,
        desktopMinWidth: DESKTOP_MIN_WIDTH,
        viewportWidth: window.innerWidth,
        ctaAllowed: ctaAllowedGlobally(),
        ctasInDom: findCtas().length,
        cartLines: [].map.call(lines, function (line) {
          return {
            key: line.getAttribute('data-cart-line-key'),
            eligible: line.getAttribute('data-rx-eligible') === 'true',
            metafield: line.getAttribute('data-rx-meta') || null,
            productType: line.getAttribute('data-rx-product-type') || null,
            rxGroup: line.getAttribute('data-rx-group') || null
          };
        })
      };

      emit('log', ['current state:', report]);
      return report;
    }
  };

  function boot() {
    watchForCtas();
    watchForUpcart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
